import React, { useContext, useEffect, useState } from 'react'

import { useDispatch } from 'react-redux'

import { Backdrop, Button, CircularProgress, InputLabel, MenuItem, Select, Tooltip, Typography } from '@mui/material'

import { RowsContext } from '../../pages/TablePage'
import config from '../../config/config'
import ConfirmDialog from '../dialogs/ConfirmDialog'

import useRole from '../../useUserRole'

import tableApi from '../../api/table'
import { setSystemMessage } from '../../features/systemMessageSlice'

import { removeSessionFilters } from '../../useSessionFilters'
import {
  ActionBox,
  ActionButtonItem,
  ActionDivButtonGroup,
  ActionFormControl,
} from '../../styles/components/TableActionsInputStyle'
import { alpha, useTheme } from '@mui/material/styles'

const TableActionsInput = () => {
  const theme = useTheme()

  const { userRole } = useRole()
  const { selectedRows, filters, maxRowsCount, getNewData, setFilters, isLoading } = useContext(RowsContext)
  const defaultExportValue = config.SAS.intervals[0].valueInMinutes
  const exportLimit = config.csvExportRowsLimit
  const [exportInterval, setExportInterval] = useState(defaultExportValue)

  const dispatch = useDispatch()

  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [exporting, setExporting] = useState(false)

  const exportCount = (sas = false) => {
    if (selectedRows.size !== 0) return `selected (${selectedRows.size})`
    if (Object.keys(filters).length !== 0) return `Filtered (${maxRowsCount})`
    return sas ? '(Rules)' : '(All)'
  }

  const exportLimitExceeded = () => {
    return maxRowsCount === null || maxRowsCount > exportLimit
  }

  const getIntervalSettings = () => {
    tableApi.getSettings().then((res) => {
      setExportInterval(res.data.send_to_SAS_interval)
    })
  }

  useEffect(() => {
    getIntervalSettings()
  }, [])

  const handleIntervalChange = (e) => {
    setExportInterval(e.target.value)
    tableApi
      .schedulerUpdate(e.target.value)
      .then((res) => {
        res.status && dispatch(setSystemMessage({ message: `Interval was successfully changed`, severity: 'success' }))
      })
      .catch((error) => {
        dispatch(setSystemMessage({ message: `Interval change failed with error ${error.message}`, severity: 'error' }))
      })
  }

  const handleExport = async () => {
    await dispatch(setSystemMessage({ message: `CSV export has started`, severity: 'success' }))
    setExporting(true)
    await tableApi
      .export([...selectedRows], filters)
      .then((res) => {
        const blob = new Blob([res.data], { type: 'data:text/csvcharset=utf-8,' })
        const blobURL = window.URL.createObjectURL(blob)
        let anchor = document.createElement('a')
        anchor.download = `export_table_${new Date().toJSON().slice(0, 10)}.csv`
        anchor.href = blobURL
        anchor.dataset.downloadurl = ['text/csv', anchor.download, anchor.href].join(':')
        anchor.click()
      })
      .catch((error) => {
        dispatch(setSystemMessage({ message: `Export failed with error ${error.message}`, severity: 'error' }))
      })
      .finally(() => {
        setExporting(false)
      })
  }

  const handleDialog = () => {
    setIsDialogOpen(!isDialogOpen)
  }

  const shouldExportDialogOpen =
    (selectedRows.size >= 100 || (selectedRows.size === 0 && maxRowsCount >= 100)) && exportCount(true) !== '(Rules)'

  const handleExportDialog = () => {
    if (shouldExportDialogOpen) {
      handleDialog()
      return
    }

    handleExportSAS()
  }

  const handleExportSAS = async () => {
    if (shouldExportDialogOpen) handleDialog()

    await tableApi
      .exportSAS([...selectedRows], filters)
      .then((res) => {
        res.status &&
          dispatch(setSystemMessage({ message: `Export to SAS was successfully executed`, severity: 'success' }))
      })
      .catch((error) => {
        dispatch(setSystemMessage({ message: `Export to SAS failed with error ${error.message}`, severity: 'error' }))
      })
  }

  const handleFiltersClear = () => {
    setFilters({})
    removeSessionFilters()
  }

  const RenderMenuItems = config.SAS.intervals.map((interval, index) => {
    return (
      <MenuItem key={index} value={interval.valueInMinutes}>
        {interval.displayName}
      </MenuItem>
    )
  })

  const isAdmin = userRole === 'admin'

  return (
    <>
      <ActionBox className={'sticky-vertical'}>
        <ActionDivButtonGroup>
          <ActionButtonItem variant="contained" color="primary" onClick={handleExportDialog} disabled={!isAdmin}>
            Export to SAS {exportCount(true)}
          </ActionButtonItem>

          <ActionFormControl variant="outlined" disabled={!isAdmin}>
            <InputLabel id="export-label">Export to SAS every</InputLabel>
            <Select
              labelId="export-label"
              value={exportInterval}
              onChange={handleIntervalChange}
              label="Export to SAS every:"
              variant="standard"
            >
              {RenderMenuItems}
            </Select>
          </ActionFormControl>
        </ActionDivButtonGroup>
        <ActionDivButtonGroup>
          <ActionButtonItem variant="contained" color="primary" onClick={() => getNewData()}>
            Refresh data
          </ActionButtonItem>
          <ActionButtonItem variant="contained" color="primary" onClick={handleFiltersClear}>
            Clear filters
          </ActionButtonItem>
          {exportLimitExceeded() || isLoading ? (
            <Tooltip
              title={
                isLoading
                  ? 'Not able to export when loading filters'
                  : 'For export filter must be set and less than ' + exportLimit + ' rows!'
              }
            >
              <span>
                <ActionButtonItem disabled={true} variant="contained" color="primary">
                  Export {exportCount()}
                </ActionButtonItem>
              </span>
            </Tooltip>
          ) : (
            <ActionButtonItem disabled={exporting} variant="contained" color="primary" onClick={handleExport}>
              Export {exportCount()}
            </ActionButtonItem>
          )}
        </ActionDivButtonGroup>
      </ActionBox>
      <ConfirmDialog
        isDialogOpen={isDialogOpen}
        title={`${exportCount(true)} records will be sent to SAS.`}
        description="Are you sure?"
        handleDialog={handleDialog}
      >
        <Button onClick={handleDialog} color="default">
          Cancel
        </Button>
        <Button onClick={handleExportSAS} color="primary" autoFocus>
          Confirm
        </Button>
      </ConfirmDialog>
      <Backdrop
        open={exporting}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: alpha(theme.palette.primary.dark, 0.9),
          flexDirection: 'column',
        }}
      >
        <CircularProgress size={80} />
        <Typography color="primary.light" sx={{ mt: 3, fontSize: '1.5rem' }}>
          Wait, please…
        </Typography>
        <Button variant="contained" color="secondary" sx={{ mt: 3, visibility: 'hidden' }}>
          Cancel
        </Button>
      </Backdrop>
    </>
  )
}

export default TableActionsInput
